import { createPortal } from "react-dom";
import { useRef } from "react";
import Button from "../../ui/Button";
import CloseButton from "../../ui/CloseButton";
import useEscapeKey from "../../../_hooks/useEscapeKey";
import "./_modal-shell.scss";

/**
 * Generic modal shell - replaces src/modals/ModalWrapper for `_`-tree
 * consumers. Portals to document.body; every dismiss trigger (ESC, a
 * genuine overlay click, the header close button) funnels into a single
 * `onRequestClose` - this component has no opinion on what "close" means
 * (immediate close vs. an unsaved-changes confirmation is entirely the
 * caller's call, kept deliberately out of here so this stays reusable).
 *
 * The overlay click check requires BOTH mousedown and click to land on the
 * overlay element itself (not just click) - a mousedown that starts inside
 * the form (e.g. selecting text in a field) and releases over the backdrop
 * would otherwise false-positive as an outside click.
 *
 * Deliberately does not hijack Enter-to-submit the way the old ModalWrapper
 * did (it captured Enter globally, including while a nested confirm dialog
 * was open). Consumers that want Enter-to-submit wrap their own content in
 * a native `<form onSubmit>` - the browser already does the right thing
 * per-input (submits from a text field, inserts a newline in a textarea)
 * with no hand-rolled key capture needed.
 *
 * `centerComponent`: absolutely centered in the header, same slot/CSS
 * approach as `PageHeaderBar`'s own `centerComponent` (see its scss) -
 * a multi-step wizard modal hands its `WizardSteps` in here instead of
 * rendering its own progress row in the body, so title/steps/actions all
 * live in the one header row instead of a title row plus a separate
 * steps row.
 *
 * `isSubmitting`: while a create/update mutation is in flight, every dismiss
 * path (ESC, overlay click, the header X) is suppressed and every header/
 * footer button is force-disabled - a caller doesn't need to thread
 * `disabled` onto each button individually just to stop double-submits or
 * an accidental close mid-request. A button that's already `loading` (the
 * one actually submitting) keeps its own spinner instead of just going
 * inert like its siblings.
 *
 * `description`: one-line blurb under `title` (the design's "Creates an
 * account record. Billing details can follow later." under "Add company").
 * `footerHint`: left-aligned helper text in the footer, ahead of
 * `footerButtons` (the design's "Owner defaults to you") - both optional,
 * omitted entirely when not passed.
 */
const ModalShell = ({
  isOpen,
  onRequestClose,
  title,
  description,
  centerComponent,
  footerHint,
  footerButtons = [],
  headerButtons = [],
  width = "400px",
  height = "auto",
  center = true,
  top,
  left,
  right,
  bottom,
  isNoScroll = false,
  isSubmitting = false,
  className = "",
  children,
}) => {
  const mouseDownOnOverlay = useRef(false);

  useEscapeKey(isOpen && !isSubmitting, onRequestClose);

  if (!isOpen) return null;

  const handleRequestClose = () => {
    if (isSubmitting) return;
    onRequestClose();
  };

  // Centered mode: the overlay's own flexbox centering is enough - no
  // manual position math needed. Non-centered mode (e.g. a right-docked
  // drawer) needs an explicit fixed anchor, since a flex child can't be
  // pinned to specific edges via its parent's alignment alone.
  const positionStyle = center
    ? {}
    : {
        position: "fixed",
        top: top ?? "auto",
        left: left ?? "auto",
        right: right ?? "auto",
        bottom: bottom ?? "auto",
      };

  // Rounded corners only make sense on a modal that's actually an island in
  // the middle of the screen - a right/left-docked drawer (center=false)
  // butts up against the viewport edge, and a fullscreen wizard host
  // (width/height 100vw/100vh, still `center`d but covering the whole
  // viewport) has no visible corner to round either.
  const isFullViewport = width === "100vw" && height === "100vh";
  const isRounded = center && !isFullViewport;
  // The only real non-centered shape today: docked to the right edge
  // (right={0}) - CompanyFormModal/MarketFormModal/ExportModal/etc. Gets
  // the Liquid Glass drawer treatment (see _modal-shell.scss); a left/top/
  // bottom-docked modal (no current caller) just falls back to the
  // undecorated square shape below, same as before this change.
  const isDockedRight = !center && right !== undefined;

  return createPortal(
    <div
      className="modal-shell__overlay"
      onMouseDown={(e) => {
        mouseDownOnOverlay.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && mouseDownOnOverlay.current) {
          handleRequestClose();
        }
        mouseDownOnOverlay.current = false;
      }}
    >
      <div
        className={`modal-shell__content ${isRounded ? "modal-shell__content--rounded" : ""} ${
          isDockedRight ? "modal-shell__content--docked-right" : ""
        } ${className}`}
        style={{ width, height, ...positionStyle }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-shell-title"
      >
        <div className="modal-shell__header">
          <div className="modal-shell__heading">
            <h2 id="modal-shell-title" className="modal-shell__title">
              {title}
            </h2>
            {description && (
              <p className="modal-shell__description">{description}</p>
            )}
          </div>
          {centerComponent && (
            <div className="modal-shell__center">{centerComponent}</div>
          )}
          <div className="modal-shell__header-buttons">
            {headerButtons.map((btn, idx) => (
              <Button
                key={idx}
                {...btn}
                disabled={isSubmitting || btn.disabled}
              />
            ))}
            <CloseButton onClick={handleRequestClose} disabled={isSubmitting} />
          </div>
        </div>

        <div
          className={`modal-shell__body ${isNoScroll ? "modal-shell__body--no-scroll" : ""}`}
        >
          {children}
        </div>

        {(footerButtons.length > 0 || footerHint) && (
          <div className="modal-shell__footer">
            {footerHint && (
              <div className="modal-shell__footer-hint">{footerHint}</div>
            )}
            {footerButtons.map((btn, idx) => (
              <Button
                key={idx}
                {...btn}
                disabled={isSubmitting || btn.disabled}
              />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ModalShell;
